/**
 * "Filter" — the home feed's filter sheet (issue #3, variant B): a bottom
 * sheet on mobile, a centred dialog on desktop. Replaces the old category
 * pills / desktop sidebar / keyword dropdown / "Til gjennomgang" chip.
 *
 * Groups: Kategori (single-select chips), Nøkkelord (a search box over
 * multi-select chips — same AND semantics as before), Status (the "Til
 * gjennomgang" switch with its recipe count). Filters apply LIVE (the feed
 * behind updates as you tap); "Vis oppskrifter" just closes, "Nullstill"
 * clears every filter. The state itself lives in `HomePage`; this is purely
 * presentational.
 */

import React, { useMemo, useRef, useState } from "react";

import { useDialogA11y } from "../lib/useDialogA11y";

export function FilterSheet({
  categories,
  allTags,
  category,
  tags,
  reviewOnly,
  reviewCount,
  onCategory,
  onToggleTag,
  onReviewOnly,
  onReset,
  restoreFocusRef,
  onClose,
}: {
  categories: string[];
  allTags: string[];
  category: string | null;
  tags: string[];
  reviewOnly: boolean;
  /** Recipes currently flagged `needs_review` (the Status group's count). */
  reviewCount: number;
  onCategory: (category: string | null) => void;
  onToggleTag: (tag: string) => void;
  onReviewOnly: (on: boolean) => void;
  onReset: () => void;
  restoreFocusRef: React.RefObject<HTMLElement | null>;
  onClose: () => void;
}): React.JSX.Element {
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  useDialogA11y({ panelRef, initialFocusRef: closeRef, restoreFocusRef, onClose });
  const [query, setQuery] = useState("");

  const visibleTags = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q === "" ? allTags : allTags.filter((t) => t.toLowerCase().includes(q));
  }, [allTags, query]);

  // Hidden at 0 — EXCEPT while the filter is on, so the only way to turn it
  // off never disappears from under the user (approving the last recipe).
  const showStatus = reviewCount > 0 || reviewOnly;

  return (
    <div
      className="dlg-overlay"
      data-testid="filter-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        className="dlg-panel sheet-dialog"
        data-testid="filter-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="filter-title"
      >
        <div className="dlg-sheet-handle" aria-hidden="true" />
        <div className="sheet-h">
          <h2 id="filter-title" className="sheet-title">
            Filter
          </h2>
          <button
            ref={closeRef}
            type="button"
            className="sheet-x"
            aria-label="Lukk"
            data-testid="filter-close"
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        {categories.length > 0 && (
          <div className="fgroup">
            <h3>Kategori</h3>
            <div className="fchips" role="group" aria-label="Kategori">
              {categories.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`fchip${category === c ? " is-on" : ""}`}
                  data-testid={`category-${c}`}
                  aria-pressed={category === c}
                  onClick={() => onCategory(category === c ? null : c)}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        )}

        {allTags.length > 0 && (
          <div className="fgroup">
            <h3>Nøkkelord</h3>
            <input
              type="search"
              className="fsearch"
              placeholder="Søk i nøkkelord …"
              aria-label="Søk i nøkkelord"
              data-testid="keyword-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <div className="fchips fchips-scroll" role="group" aria-label="Nøkkelord">
              {visibleTags.length === 0 ? (
                <p className="fempty" data-testid="keyword-empty">
                  Ingen nøkkelord passer søket.
                </p>
              ) : (
                visibleTags.map((t) => (
                  <button
                    key={t}
                    type="button"
                    className={`fchip${tags.includes(t) ? " is-on" : ""}`}
                    data-testid={`tag-${t}`}
                    aria-pressed={tags.includes(t)}
                    onClick={() => onToggleTag(t)}
                  >
                    {t}
                  </button>
                ))
              )}
            </div>
          </div>
        )}

        {showStatus && (
          <div className="fgroup">
            <h3>Status</h3>
            <button
              type="button"
              role="switch"
              aria-checked={reviewOnly}
              className="ftoggle"
              data-testid="review-filter"
              onClick={() => onReviewOnly(!reviewOnly)}
            >
              <span>
                Til gjennomgang
                <span className="ftoggle-count" data-testid="review-count">
                  {reviewCount} {reviewCount === 1 ? "oppskrift" : "oppskrifter"}
                </span>
              </span>
              <span className={`fsw${reviewOnly ? " is-on" : ""}`} aria-hidden="true" />
            </button>
          </div>
        )}

        <div className="sheet-actions">
          <button
            type="button"
            className="btn btn-ghost"
            data-testid="filter-reset"
            onClick={onReset}
          >
            Nullstill
          </button>
          <button
            type="button"
            className="btn btn-primary"
            data-testid="filter-apply"
            onClick={onClose}
          >
            Vis oppskrifter
          </button>
        </div>
      </div>
    </div>
  );
}
